/**
 * SearchableMultiSelect.tsx - Searchable Multi-Select List Component
 *
 * Ultra-lightweight implementation that defers ALL heavy work.
 * Only processes visible items, everything else is lazy.
 */

import React, { useState, useMemo, useCallback, useRef, startTransition } from 'react';
import { VStack, HStack, Input, Text, Box, Spinner } from '@chakra-ui/react';
import { Checkbox } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FilterOption } from '@/types/filter.types';
import { LuSearch } from 'react-icons/lu';
import { useEntityValueSearch } from '@/hooks/useEntityValueSearch';

interface SearchableMultiSelectProps {
    options: FilterOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    emptyMessage?: string;
    loading?: boolean;
    maxHeight?: number;
    // Set both to make the search box query entity_values for this field instead of
    // only substring-matching the options already loaded. `searchField` is an
    // entity_values field key (authors, persons, organizations, locations).
    searchField?: string;
    circleId?: string;
}

const ITEM_HEIGHT = 36;
const VISIBLE_ITEMS = 8; // Only render what's visible

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
    options,
    selectedValues,
    onChange,
    placeholder,
    emptyMessage,
    loading = false,
    maxHeight = 240,
    searchField,
    circleId,
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const [searchQuery, setSearchQuery] = useState('');
    const [scrollTop, setScrollTop] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    // Create a Set for O(1) lookup - this is cheap
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    const trimmedQuery = searchQuery.trim();

    const { results: serverResults, loading: searching } = useEntityValueSearch({
        circleId: circleId || '',
        field: searchField || '',
        query: searchQuery,
    });

    // A value found by search is, by design, outside the top-N `options` slice - so
    // once the query is cleared its row would vanish and the filter could no longer
    // be unchecked from this list. Pin every selected value `options` doesn't carry.
    // Both endpoints build options with label === value === displayLabel, so a
    // synthesized row is indistinguishable from a real one apart from the count.
    const baseOptions = useMemo(() => {
        const known = new Set(options.map((opt) => opt.value));
        const pinned = selectedValues
            .filter((value) => !known.has(value))
            .map((value) => ({ label: value, value, count: 0, displayLabel: value }));
        return pinned.length ? [...pinned, ...options] : options;
    }, [options, selectedValues]);

    // Simple filter - no sorting, just filter by search
    const filteredOptions = useMemo(() => {
        if (!trimmedQuery) {
            return baseOptions;
        }
        const query = trimmedQuery.toLowerCase();
        return baseOptions.filter(
            (opt) =>
                opt.displayLabel.toLowerCase().includes(query) ||
                opt.label.toLowerCase().includes(query)
        );
    }, [baseOptions, trimmedQuery]);

    // Server hits are appended, not substituted: substring-matching the ~100 loaded
    // options routinely yields more rows than one page of hits, so replacing the
    // local list would lose matches. Appending also means a failed or blocked
    // request simply contributes nothing and the box behaves exactly as before.
    // Appending at the tail keeps rows already on screen where they are, so the
    // virtual-scroll offset stays valid when a response lands.
    const displayOptions = useMemo(() => {
        if (!trimmedQuery || !serverResults.length) {
            return filteredOptions;
        }
        const shown = new Set(filteredOptions.map((opt) => opt.value));
        const extras = serverResults.filter((opt) => !shown.has(opt.value));
        return extras.length ? [...filteredOptions, ...extras] : filteredOptions;
    }, [filteredOptions, serverResults, trimmedQuery]);

    // Calculate which items are visible based on scroll position
    const { visibleItems, startIndex, totalHeight } = useMemo(() => {
        const total = displayOptions.length;
        const totalHeight = total * ITEM_HEIGHT;
        const start = Math.floor(scrollTop / ITEM_HEIGHT);
        const visibleCount = Math.min(VISIBLE_ITEMS + 2, total - start); // +2 for buffer
        
        // Get only the visible slice - NO SORTING of full array
        const visible = displayOptions.slice(start, start + visibleCount);
        
        return {
            visibleItems: visible,
            startIndex: start,
            totalHeight,
        };
    }, [displayOptions, scrollTop]);

    const handleToggle = useCallback((value: string) => {
        const newValues = selectedSet.has(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
        onChange(newValues);
    }, [selectedSet, selectedValues, onChange]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        // Use startTransition to keep scroll smooth
        startTransition(() => {
            setScrollTop(e.currentTarget.scrollTop);
        });
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setScrollTop(0); // Reset scroll on search
    }, []);

    const listHeight = Math.min(displayOptions.length * ITEM_HEIGHT, maxHeight);

    if (loading) {
        return (
            <HStack justifyContent="center" padding={4}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.500">{t('loading') || 'Loading...'}</Text>
            </HStack>
        );
    }

    return (
        <VStack align="stretch" gap={2}>
            {/* Search Input */}
            <Box position="relative">
                <Input
                    size="sm"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={placeholder || t('filter_search') || 'Search...'}
                    paddingLeft={isRTL ? '8px' : '32px'}
                    paddingRight={isRTL ? '32px' : '8px'}
                    // Not the UI direction: this box exists to look up Arabic and
                    // Sorani values, which an English-UI user must be able to type.
                    dir="auto"
                />
                <Box
                    position="absolute"
                    top="50%"
                    left={isRTL ? 'auto' : '8px'}
                    right={isRTL ? '8px' : 'auto'}
                    transform="translateY(-50%)"
                    color="gray.400"
                    pointerEvents="none"
                >
                    {searching ? <Spinner size="xs" /> : <LuSearch size={16} />}
                </Box>
            </Box>

            {/* Options List */}
            {displayOptions.length === 0 ? (
                <Text fontSize="sm" color="gray.500" padding={4} textAlign="center">
                    {trimmedQuery
                        ? t('filter_no_matches') || 'No matches'
                        : emptyMessage || t('filter_no_options') || 'No options available'}
                </Text>
            ) : (
                <Box
                    ref={containerRef}
                    height={`${listHeight}px`}
                    overflowY="auto"
                    onScroll={handleScroll}
                    borderRadius="4px"
                    dir={isRTL ? 'rtl' : 'ltr'}
                    css={{
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
                    }}
                >
                    {/* Spacer for virtual scroll */}
                    <Box height={`${totalHeight}px`} position="relative">
                        {/* Only render visible items */}
                        <Box
                            position="absolute"
                            top={`${startIndex * ITEM_HEIGHT}px`}
                            left={0}
                            right={0}
                        >
                            {visibleItems.map((option) => {
                                const isSelected = selectedSet.has(option.value);
                                return (
                                    <Box
                                        key={option.value}
                                        height={`${ITEM_HEIGHT}px`}
                                        paddingX={2}
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <Box
                                            flex="1"
                                            paddingY={1}
                                            paddingX={2}
                                            borderRadius="4px"
                                            _hover={{ background: '#F5F5F5' }}
                                        >
                                            <Checkbox.Root
                                                checked={isSelected}
                                                onCheckedChange={() => handleToggle(option.value)}
                                                cursor="pointer"
                                            >
                                                <HStack width="100%" gap={2} dir={isRTL ? 'rtl' : 'ltr'}>
                                                    <Checkbox.HiddenInput />
                                                    <Checkbox.Control cursor="pointer" />
                                                    <Checkbox.Label cursor="pointer" flex="1">
                                                        <Text fontSize="sm" color="gray.700" lineClamp={1} dir="auto">
                                                            {option.displayLabel}
                                                        </Text>
                                                    </Checkbox.Label>
                                                    {option.count > 0 && (
                                                        <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                                            ({option.count})
                                                        </Text>
                                                    )}
                                                </HStack>
                                            </Checkbox.Root>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}
        </VStack>
    );
};